import { useEffect } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useGymItems, useGyms } from "@/features/gyms";
import { Deck, useActiveSession } from "@/features/sessions";

/**
 * TODAY — the first tab and the app's front door.
 *
 * ⚠️ THE ROUTE RENDERS NO SCREEN TITLE. The real screen's heading is the GREETING — "Good
 * afternoon, Temo" — which is inside the deck because it sits in a header row beside the gym
 * pill and above the date, and a title stacked on top of it would be two headings saying the
 * same thing. So this file is the frame and the gate, and nothing else.
 */
export const Route = createFileRoute("/_app/")({
  component: DeckScreen,
});

function DeckScreen() {
  const navigate = useNavigate();
  const { isPending, isError } = useGyms();
  const gyms = useGymItems();

  /**
   * 🚨 A SESSION THAT IS UNDER WAY OWNS THIS TAB. Starting one navigates to it, and every tap
   * on the tab bar after that used to land on a fresh deck — so an athlete who checked their
   * gym between two blocks came back to a stack of suggestions with no way to their own
   * half-finished workout except the back gesture, which the tab switch had already spent.
   *
   * ⚠️ IT IS A REDIRECT AND NOT A BANNER, on the owner's instruction: "it should go directly
   * to the session clicking today". A row offering to resume was the first attempt and it is
   * the wrong shape for this — Today is where you go to train, and while a session is open
   * that session IS today. The deck comes back the moment it ends, either way: finishing it
   * writes the log and the ✕ abandons it, and both clear the record this reads.
   *
   * `undefined` while the phone's store is being read, which is why the guard names the two
   * states apart: redirecting on "not known yet" would bounce every cold launch.
   */
  const activeSession = useActiveSession();

  /**
   * 🚨 NO CARDS BEFORE SETUP IS DONE — and "setup is done" means A ROOM EXISTS.
   *
   * This reverses BUD-293's decision, deliberately and on the owner's instruction. That issue
   * shipped a BANNER above the deck on the grounds that a brand-new account should reach a
   * working deck either way. It does reach one, and that turned out to be the problem: an
   * athlete with no room is planned for against `inventory.floorRoom()` — you and the floor —
   * so every card is bodyweight, none of them has a strength block, and the first thing a new
   * account sees is the app at its worst while a banner explains why. Sending them into the
   * flow instead is one screen of work in exchange for a deck that is right the first time.
   *
   * THE ROOM IS THE ONLY THING GATED, and nothing else in the flow is. Every other question
   * has a floor the engine already assumes (rung zero, `beginner`, a population max, no
   * restrictions), so an athlete who walks past the rest still gets valid sessions — which is
   * why the confirmation screen at the end of the flow is reachable with four steps skipped.
   *
   * ⚠️ A FAILED READ IS NOT AN EMPTY ONE. `isError` means the rooms could not be fetched — a
   * phone on a train — and bouncing that athlete into setup would ask them to re-enter a gym
   * they already have. The deck stays; it will show its own cached cards or its own failure.
   */
  const needsSetup = !isPending && !isError && gyms.length === 0;

  useEffect(() => {
    // `replace`, so the back gesture out of first run does not land on the deck that sent
    // them there and immediately send them back.
    if (needsSetup) void navigate({ to: "/first-run", replace: true });
  }, [needsSetup, navigate]);

  useEffect(() => {
    /* `replace` for the same reason: the deck is not a place they came FROM, so it must not be
       the place a back gesture out of the session returns to. */
    if (activeSession)
      void navigate({ to: "/session/$id", params: { id: activeSession }, replace: true });
  }, [activeSession, navigate]);

  /**
   * ⚠️ THE DECK IS MOUNTED WHILE THE ROOMS ARE STILL IN THE AIR, and that is a trade rather
   * than an oversight. Blanking the front door until a second request answers would put an
   * empty screen in front of every cold launch to protect a case that only happens once. Both
   * requests leave together, the suggestions payload is by far the larger of the two, and the
   * deck draws a card-shaped skeleton until its own lands — so what the athlete being sent to
   * setup actually sees is the skeleton, not a card.
   */
  return needsSetup || activeSession ? null : <Deck />;
}
